import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Users, 
  Clock, 
  CheckCircle, 
  FileCode,
  Euro,
  ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [creditor, setCreditor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, creditorRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/creditor/config`)
        ]);
        setStats(statsRes.data);
        setCreditor(creditorRes.data);
      } catch (e) {
        console.error("Error fetching dashboard data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="h-8 w-48 bg-slate-200 rounded animate-pulse mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-slate-200 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const kpis = [
    {
      title: "Clienti Attivi",
      value: stats?.clienti_count || 0,
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      title: "Scadenze in Attesa",
      value: stats?.scadenze_pending || 0,
      icon: Clock,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
    },
    {
      title: "Importo da Incassare",
      value: `€ ${(stats?.pending_amount || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`,
      icon: Euro,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
  ];

  return (
    <div className="animate-fade-in space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Panoramica incassi SEPA B2B</p>
        </div>
        <Link to="/genera-xml">
          <Button data-testid="generate-xml-cta" className="gap-2">
            Genera XML
            <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="kpi-grid">
        {kpis.map((kpi, idx) => (
          <Card key={idx} className="kpi-card" data-testid={`kpi-${idx}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{kpi.title}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-2">{kpi.value}</p>
                </div>
                <div className={`${kpi.bgColor} p-3 rounded-lg`}>
                  <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Creditor Info */}
        <Card data-testid="creditor-info">
          <CardHeader>
            <CardTitle className="text-lg">Dati Creditore</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Ragione Sociale</p>
              <p className="text-sm font-medium text-slate-900 mt-1">{creditor?.name}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">IBAN</p>
                <p className="text-sm font-mono text-slate-900 mt-1">{creditor?.iban}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">BIC</p>
                <p className="text-sm font-mono text-slate-900 mt-1">{creditor?.bic}</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Creditor ID (CID)</p>
              <p className="text-sm font-mono text-slate-900 mt-1">{creditor?.creditor_id}</p>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card data-testid="quick-stats">
          <CardHeader>
            <CardTitle className="text-lg">Statistiche Rapide</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium">Scadenze Elaborate</span>
                </div>
                <span className="text-lg font-bold text-slate-900">{stats?.scadenze_processed || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileCode className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium">File XML Generati</span>
                </div>
                <span className="text-lg font-bold text-slate-900">{stats?.xml_generations_count || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Generations */}
      {stats?.recent_generations?.length > 0 && (
        <Card data-testid="recent-generations">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Ultime Generazioni</CardTitle>
            <Link to="/storico">
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900">
                Vedi tutte
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Data</th>
                    <th className="text-right">Transazioni</th>
                    <th className="text-right">Importo</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_generations.map((gen) => (
                    <tr key={gen.id}>
                      <td className="font-mono text-xs">{gen.filename}</td>
                      <td>{new Date(gen.created_at).toLocaleDateString('it-IT')}</td>
                      <td className="text-right tabular-nums">{gen.num_transactions}</td>
                      <td className="text-right tabular-nums font-medium">
                        € {gen.total_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
